import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './store/auth.js';
import { isPublicRoute, shouldMountAppUtilities } from './lib/publicRoutes.js';
import { apiGet } from './lib/api.js';

import Login from './pages/Login.jsx';
import Signup from './pages/Signup.jsx';
import AcceptInvite from './pages/AcceptInvite.jsx';
import ForgotPassword from './pages/ForgotPassword.jsx';
import ResetPassword from './pages/ResetPassword.jsx';
import Landing from './pages/Landing.jsx';
import Layout from './components/Layout.jsx';
import Dashboard from './pages/Dashboard.jsx';
import CallHistory from './pages/CallHistory.jsx';
import CallScorecardView from './pages/scorecards/CallScorecardView.jsx';
import Leads from './pages/Leads.jsx';
import LeadDetail from './pages/LeadDetail.jsx';
import Inbox from './pages/Inbox.jsx';
import Dialer from './pages/Dialer.jsx';
import ScriptsPopout from './pages/ScriptsPopout.jsx';
import Calendar from './pages/Calendar.jsx';
import Reports from './pages/Reports.jsx';
import Settings from './pages/Settings.jsx';
import SchedulerSettings from './pages/agent/SchedulerSettings.jsx';
import BookingPage from './pages/BookingPage.jsx';
import PublicBusinessCard from './pages/PublicBusinessCard.jsx';
import PublicProducerPage from './pages/PublicProducerPage.jsx';
import Security from './pages/Security.jsx';
import AuditLogPage from './pages/AuditLog.jsx';
import Admin from './pages/Admin.jsx';
import DevTools from './pages/DevTools.jsx';
import Vendors from './pages/Vendors.jsx';
import LeadVendorForm from './pages/LeadVendorForm.jsx';
import SalesPipeline from './pages/SalesPipeline.jsx';
import PhoneSetup from './pages/PhoneSetup.jsx';
import ProducerAgreement from './pages/ProducerAgreement.jsx';
import PhoneSetupTollFree from './pages/PhoneSetupTollFree.jsx';
import InboundRouting from './pages/InboundRouting.jsx';
import MarketingHub from './pages/MarketingHub.jsx';
import DripBuilder from './pages/marketing/DripBuilder.jsx';
import MarketingDripDetail from './pages/marketing/DripDetail.jsx';
import Billing from './pages/Billing.jsx';
import Support from './pages/Support.jsx';
import LeadImport from './pages/LeadImport.jsx';
import AIBotStudio from './pages/AIBotStudio.jsx';
import KnowledgeBase from './pages/ai/KnowledgeBase.jsx';
import A2PWizard from './pages/A2PWizard.jsx';
import BusinessProfile from './pages/BusinessProfile.jsx';
import DncCompliance from './pages/DncCompliance.jsx';
import ComplianceLog from './pages/compliance/ComplianceLog.jsx';
import Carriers from './pages/Carriers.jsx';
import StatusPage from './pages/StatusPage.jsx';
import Voicemail from './pages/Voicemail.jsx';
import PublicBooking from './pages/PublicBooking.jsx';
import PublicRoundRobinBooking from './pages/PublicRoundRobinBooking.jsx';
import AIDialer from './pages/AIDialer.jsx';
import AIDialerCall from './pages/AIDialerCall.jsx';
import AIDialerCampaign from './pages/AIDialerCampaign.jsx';
import AIDialerCampaignCreate from './pages/AIDialerCampaignCreate.jsx';
import AIDialerBotTrainer from './pages/AIDialerBotTrainer.jsx';
import AIDialerScreeners from './pages/AIDialerScreeners.jsx';
import { AI_DIALER_ENABLED, useAIDialerEnabled } from './lib/aiDialerFlag.js';
import { useAITextAgentEnabled } from './lib/aiTextAgentFlag.js';
import AIUsage from './pages/AIUsage.jsx';
import AdminPL from './pages/AdminPL.jsx';
import AgentDigest from './pages/AgentDigest.jsx';
import CivixAssistant from './pages/CivixAssistant.jsx';
import LeadDistribution from './pages/LeadDistribution.jsx';
import LeadOrders from './pages/LeadOrders.jsx';
import LeadStatusGroups from './pages/LeadStatusGroups.jsx';
import Leaderboard from './pages/Leaderboard.jsx';
import LeadRoi from './pages/LeadRoi.jsx';
import VendorLeadROI from './pages/growth/LeadROI.jsx';
import GoogleAds from './pages/GoogleAds.jsx';
import IntegrationsGoogleAds from './pages/IntegrationsGoogleAds.jsx';
import AdStudio from './pages/AdStudio.jsx';
import Roleplay from './pages/Roleplay.jsx';
import RoleplayLive from './pages/RoleplayLive.jsx';
import RolePlayCoach from './views/RolePlayCoach.jsx';
import SmsKeywords from './pages/SmsKeywords.jsx';
import SyncAccount from './pages/SyncAccount.jsx';
import MyTeam from './pages/admin/MyTeam.jsx';
import UnderwritingBot from './pages/UnderwritingBot.jsx';
import VoiceStudio from './pages/VoiceStudio.jsx'; // Unified: Quick Clone + Advanced Studio
import BotsHub from './pages/BotsHub.jsx';
import BotDetail from './pages/BotDetail.jsx';
import TextBotDetail from './pages/TextBotDetail.jsx';
import AgedLeads from './pages/AgedLeads.jsx';
import AgedLeadsPowerDial from './pages/AgedLeadsPowerDial.jsx';
import PersistencyDashboard from './pages/PersistencyDashboard.jsx';
import AdminAgedLeads from './pages/AdminAgedLeads.jsx';
import AdminFeedback from './pages/AdminFeedback.jsx';
import FeedbackButton from './components/FeedbackButton.jsx';
import SOSButton from './components/SOSButton.jsx';
import { installConsoleErrorRing } from './lib/consoleErrorRing.js';
import PowerDialRunBar from './components/PowerDialRunBar.jsx';
import './lib/sentryInit.js'; // no-op until VITE_SENTRY_DSN is set
import SentryVerifyRoute from './observability/SentryVerifyRoute';
import AgreementGate from './components/onboarding/AgreementGate.jsx';
import Privacy from './pages/legal/Privacy.jsx';
import Terms from './pages/legal/Terms.jsx';
import SmsPolicy from './pages/legal/SmsPolicy.jsx';

// Install the console-error ring buffer at module load so the SOS modal
// has recent errors to attach. Idempotent — no-ops if already installed.
installConsoleErrorRing();

// Feature flag: hide AI Roleplay entirely when not explicitly enabled.
const ROLEPLAY_ENABLED = import.meta.env.VITE_ENABLE_ROLEPLAY === 'true';
// Carrier e-Apps hidden by default — no integration permission from carriers
// as of 2026-07-07. Flip VITE_ENABLE_CARRIER_EAPPS=true when approvals land.
const CARRIER_EAPPS_ENABLED = import.meta.env.VITE_ENABLE_CARRIER_EAPPS === 'true';

// AI Dialer v1 — every /ai-dialer* page is gated. When the flag is off we
// redirect to /dashboard with a toast (the toast fires once per mount so the
// user understands why nothing loaded).
// AITextAgentGate — mirrors AIDialerGate. Redirects to /dashboard with a
// toast when the tenant does not have the AI Text Agent feature enabled.
function AITextAgentGate({ children }) {
  const loc = useLocation();
  const { enabled, loading } = useAITextAgentEnabled();
  useEffect(() => {
    if (!loading && !enabled) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast('AI Text Agent is not yet enabled for your account. Contact support to enable.', { icon: 'ℹ️' });
      });
    }
  }, [loc.pathname, enabled, loading]);
  if (loading) return null;
  if (!enabled) return <Navigate to="/dashboard" replace />;
  return children;
}

function AIDialerGate({ children }) {
  const loc = useLocation();
  // Effective flag = build-time env OR per-tenant override (fetched on first
  // use). While the runtime fetch is in flight we render nothing so we don't
  // flash a redirect for enabled tenants.
  const { enabled, loading } = useAIDialerEnabled();
  useEffect(() => {
    if (!loading && !enabled) {
      import('react-hot-toast').then(({ default: toast }) => {
        toast('AI Dialer is not yet enabled for your account. Contact support to enable.', { icon: 'ℹ️' });
      });
    }
  }, [loc.pathname, enabled, loading]);
  if (loading) return null;
  if (!enabled) return <Navigate to="/dashboard" replace />;
  return children;
}

function Protected({ children }) {
  const { token } = useAuth();
  const loc = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: loc }} />;
  return children;
}

// Public root: Landing for unauthed visitors; redirect authed users into the app shell.
function PublicOrDashboard() {
  const { token } = useAuth();
  if (!token) return <Landing />;
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  const { setTenant, agent, token } = useAuth();
  const { pathname } = useLocation();
  const showAppUtilities = shouldMountAppUtilities(pathname, token, agent);

  // Public branding fetch — silent on any failure (no toast). Skipped on public routes
  // where we don't need branding (login/signup/landing) so a CORS hiccup never
  // shows a "Network Error" to a visitor.
  useEffect(() => {
    const path = window.location.pathname || '/';
    if (isPublicRoute(path)) return;
    apiGet('/tenant').then((r) => r?.tenant && setTenant(r.tenant)).catch(() => {});
  }, []);

  return (
    <>
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/welcome" element={<Landing />} />
      <Route path="/book/:slug" element={<PublicBooking />} />
      <Route path="/schedule/:slug" element={<PublicBooking />} />
      {/* Public Digital Business Card: /c/{slug} is canonical, /v/{id} keeps old shared links working. */}
      <Route path="/c/:userId" element={<PublicBusinessCard />} />
      <Route path="/v/:userId" element={<PublicBusinessCard />} />
      {/* Public Producer Validation Page (published pages only) */}
      <Route path="/p/:slug" element={<PublicProducerPage />} />
      <Route path="/schedule/rr/:token" element={<PublicRoundRobinBooking />} />

      {/* Public legal pages — no auth, indexable (no noindex). Twilio + Meta reviewers crawl these. */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/sms-policy" element={<SmsPolicy />} />

      {/* Public root — Landing for unauthed, Dashboard for authed (Dashboard requires auth via Protected on other routes) */}
      <Route path="/" element={<PublicOrDashboard />} />

      {/* Call-script pop-out window: authenticated but outside the app layout */}
      <Route path="/scripts/popout" element={<Protected><ScriptsPopout /></Protected>} />

      {/* Protected app */}
      <Route element={<Protected><AgreementGate><Layout /></AgreementGate></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/calls" element={<CallHistory />} />
        <Route path="/scorecards/:callId" element={<CallScorecardView />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/leads/:id" element={<LeadDetail />} />
        <Route path="/inbox" element={<Inbox />} />
        <Route path="/dialer" element={<Dialer />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/drips" element={<Navigate to="/marketing/drips" replace />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/growth/lead-roi" element={<VendorLeadROI />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/team" element={<Navigate to="/settings?tab=team" replace />} />
        <Route path="/settings/business-card" element={<Navigate to="/settings?tab=business-card" replace />} />
        <Route path="/settings/digital-business-card" element={<Navigate to="/settings?tab=business-card" replace />} />
        <Route path="/settings/vcard" element={<Navigate to="/settings?tab=business-card" replace />} />
        <Route path="/vcard" element={<Navigate to="/settings?tab=business-card" replace />} />
        <Route path="/booking-page" element={<BookingPage />} />
        <Route path="/appointment-booking" element={<BookingPage />} />
        <Route path="/settings/booking" element={<BookingPage />} />
        <Route path="/settings/scheduler" element={<SchedulerSettings />} />
        <Route path="/settings/security" element={<Security />} />
        <Route path="/settings/audit-log" element={<AuditLogPage />} />
        <Route path="/vendors" element={<Vendors />} />
        <Route path="/vendors/add" element={<LeadVendorForm />} />
        <Route path="/vendors/edit/:id" element={<LeadVendorForm />} />
        {/* Sidebar consolidation: Campaigns merged into Lead Vendors; Smart Ads merged into Ad Studio. */}
        <Route path="/campaigns" element={<Navigate to="/vendors" replace />} />
        <Route path="/ads" element={<Navigate to="/ad-studio" replace />} />
        {/* Sidebar consolidation (PR-A): Second Chance page deleted — was
            a duplicate of Aged Leads. Legacy links now redirect. */}
        <Route path="/second-chance" element={<Navigate to="/aged-leads" replace />} />
        <Route path="/pipeline" element={<SalesPipeline />} />
        <Route path="/phone-setup" element={<PhoneSetup />} />
        <Route path="/numbers" element={<Navigate to="/phone-setup" replace />} />
        <Route path="/onboarding/producer-agreement" element={<ProducerAgreement />} />
        <Route path="/phone-setup/toll-free" element={<PhoneSetupTollFree />} />
        <Route path="/number-health" element={<Navigate to="/phone-setup" replace />} />
        <Route path="/dialing/number-health" element={<Navigate to="/phone-setup" replace />} />
        <Route path="/inbound-routing" element={<InboundRouting />} />
        <Route path="/marketing" element={<MarketingHub />} />
        {/* Drip editor host. The browse surface (starter kits + drip flows) now
            lives in the Marketing Hub "Drip Builder" tab; this route is the
            editor that cards open (?dripGroupId=<id> pre-loads an existing drip). */}
        <Route path="/marketing-hub/drip-builder" element={<DripBuilder />} />
        <Route path="/marketing/drips/builder" element={<DripBuilder />} />
        {/* The standalone "Drip Builder" sidebar entry was removed; its browse
            tabs are merged into Marketing Hub > Drip Builder. Redirect legacy
            browse URLs there, preserving old bookmarks. */}
        <Route path="/drip-builder" element={<Navigate to="/marketing?tab=drip-builder" replace />} />
        <Route path="/drip-builder/*" element={<Navigate to="/marketing?tab=drip-builder" replace />} />
        <Route path="/marketing-hub/drip-builder/library" element={<Navigate to="/marketing?tab=drip-builder" replace />} />
        <Route path="/marketing-hub/library" element={<Navigate to="/marketing?tab=drip-builder" replace />} />
        <Route path="/marketing/library" element={<Navigate to="/marketing?tab=drip-builder" replace />} />
        <Route path="/marketing/templates" element={<Navigate to="/marketing?tab=drip-builder" replace />} />
        {/* Sidebar consolidation: standalone marketing sub-pages now redirect into
            the unified Marketing Hub tabs. Detail routes (:id) keep their dedicated pages. */}
        <Route path="/marketing/lead-types" element={<Navigate to="/marketing?tab=dispositions" replace />} />
        <Route path="/marketing/dispositions" element={<Navigate to="/marketing?tab=dispositions" replace />} />
        <Route path="/marketing/drips" element={<Navigate to="/marketing?tab=drip-builder" replace />} />
        <Route path="/marketing/drips/:id" element={<MarketingDripDetail />} />
        <Route path="/marketing/pre-set-texts" element={<Navigate to="/marketing?tab=preset-texts" replace />} />
        <Route path="/marketing/automated-actions" element={<Navigate to="/marketing?tab=drip-builder" replace />} />
        <Route path="/marketing/automated-actions/:id" element={<Navigate to="/marketing?tab=drip-builder" replace />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/support" element={<Support />} />
        <Route path="/leads/import" element={<LeadImport />} />
        <Route path="/drips/:id" element={<Navigate to="/marketing/drips" replace />} />
        <Route path="/aibots/:id" element={<AIBotStudio />} />
        <Route path="/a2p" element={<A2PWizard />} />
        <Route path="/business-profile" element={<BusinessProfile />} />
        <Route path="/dnc-compliance" element={<DncCompliance />} />
        <Route path="/compliance/log" element={<ComplianceLog />} />
        <Route
          path="/carriers"
          element={CARRIER_EAPPS_ENABLED ? <Carriers /> : <Navigate to="/dashboard" replace />}
        />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/automations" element={<Navigate to="/marketing?tab=drip-builder" replace />} />
        {/* Route aliases (audit-2026-06-05 UI-10) — catch common typo/legacy URLs */}
        <Route path="/reporting" element={<Navigate to="/reports" replace />} />
        <Route path="/sms" element={<Navigate to="/inbox" replace />} />
        <Route path="/sales-pipeline" element={<Navigate to="/pipeline" replace />} />
        <Route path="/dnc" element={<Navigate to="/dnc-compliance" replace />} />
        <Route path="/marketing-hub" element={<Navigate to="/marketing" replace />} />
        <Route path="/voicemail" element={<Voicemail />} />
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/booking-page" element={<BookingPage />} />
        <Route path="/appointment-booking" element={<BookingPage />} />
        <Route path="/settings/booking" element={<BookingPage />} />
        {/* AI / Dialing */}
        <Route path="/ai-dialer" element={<AIDialerGate><AIDialer /></AIDialerGate>} />
        <Route path="/ai-dialer/campaigns/new" element={<AIDialerGate><AIDialerCampaignCreate /></AIDialerGate>} />
        <Route path="/ai-dialer/bots/train" element={<AIDialerGate><AIDialerBotTrainer /></AIDialerGate>} />
        <Route path="/ai-dialer/screeners" element={<AIDialerGate><AIDialerScreeners /></AIDialerGate>} />
        <Route path="/settings/ai/knowledge" element={<KnowledgeBase />} />
        <Route path="/ai-dialer/call/:id" element={<AIDialerGate><AIDialerCall /></AIDialerGate>} />
        <Route path="/ai-dialer/campaign/:id" element={<AIDialerGate><AIDialerCampaign /></AIDialerGate>} />
        <Route path="/voice-studio" element={<VoiceStudio />} />
        <Route path="/voice-cloning" element={<Navigate to="/voice-studio" replace />} />
        <Route path="/voice-profiles" element={<Navigate to="/voice-studio" replace />} />
        <Route path="/civix-assistant" element={<CivixAssistant />} />
        <Route path="/underwriting-bot" element={<UnderwritingBot />} />
        <Route path="/bots" element={<BotsHub />} />
        <Route path="/bots/:id" element={<BotDetail />} />
        {/* Legacy top-level routes now redirect into the BotsHub tabs */}
        <Route path="/aibots" element={<Navigate to="/bots?tab=ai" replace />} />
        <Route path="/text-bots" element={<Navigate to="/bots?tab=text" replace />} />
        {/* Shadow Mode is now a tab inside AI Dialer; redirect preserves bookmarks. */}
        <Route path="/shadow-mode" element={<Navigate to="/ai-dialer?tab=shadow-mode" replace />} />
        <Route path="/text-bots/:id" element={<TextBotDetail />} />
        {/* PR #385 — AI Text Agent surface. Routes to the BotsHub text tab
            (single training UI); gated behind the aiTextAgentEnabled flag. */}
        <Route path="/ai-text-agent" element={<AITextAgentGate><Navigate to="/bots?tab=text" replace /></AITextAgentGate>} />
        <Route path="/ai-text-agent/:id" element={<AITextAgentGate><TextBotDetail /></AITextAgentGate>} />
        {/* v3 real-time voice roleplay (default). v2 text-chat remains at /roleplay/chat. */}
        {/* Gated behind VITE_ENABLE_ROLEPLAY; when off, /roleplay/* redirects to /dashboard. */}
        {ROLEPLAY_ENABLED && <Route path="/roleplay" element={<RoleplayLive />} />}
        {ROLEPLAY_ENABLED && <Route path="/roleplay/chat" element={<Roleplay />} />}
        {ROLEPLAY_ENABLED && <Route path="/roleplay/coach" element={<RolePlayCoach />} />}
        {!ROLEPLAY_ENABLED && <Route path="/roleplay/*" element={<Navigate to="/dashboard" replace />} />}
        {/* Growth */}
        <Route path="/sms-keywords" element={<SmsKeywords />} />
        {/* Leads pipeline */}
        <Route path="/lead-orders" element={<LeadOrders />} />
        <Route path="/sync-account" element={<SyncAccount />} />
        {/* Performance */}
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/digest" element={<AgentDigest />} />
        <Route path="/ai-usage" element={<AIUsage />} />
        {/* Admin / superadmin */}
        <Route path="/aged-leads" element={<AgedLeads />} />
        <Route path="/aged-leads/power-dial/:sessionId" element={<AgedLeadsPowerDial />} />
        <Route path="/persistency" element={<PersistencyDashboard />} />
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/admin" element={<Admin />} />}
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/admin-aged-leads" element={<AdminAgedLeads />} />}
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/team" element={<Navigate to="/settings?tab=team" replace />} />}
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/admin/my-team" element={<MyTeam />} />}
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/team/settings" element={<Navigate to="/settings?tab=team" replace />} />}
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/lead-distribution" element={<LeadDistribution />} />}
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/lead-status-groups" element={<LeadStatusGroups />} />}
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/admin-pl" element={<AdminPL />} />}
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/lead-roi" element={<LeadRoi />} />}
        {/* Meta Ads pages were retired; old bookmarks land on the dashboard. */}
        <Route path="/meta-ads/*" element={<Navigate to="/dashboard" replace />} />
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/google-ads" element={<GoogleAds />} />}
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/settings/integrations/google-ads" element={<IntegrationsGoogleAds />} />}
        {['admin', 'superadmin'].includes(agent?.role) && <Route path="/ad-studio" element={<AdStudio />} />}
        <Route path="/rotation-campaigns" element={<Navigate to="/dashboard" replace />} />
        {agent?.role === 'superadmin' && <Route path="/devtools" element={<DevTools />} />}
        {agent?.role === 'superadmin' && <Route path="/admin/feedback" element={<AdminFeedback />} />}
      </Route>
      {/* Sentry verification route — throws on mount in dev; gated in prod by VITE_SENTRY_TEST_KEY. */}
      <Route path="/__sentry_test" element={<SentryVerifyRoute />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    {showAppUtilities ? <FeedbackButton /> : null}
    {showAppUtilities ? <SOSButton /> : null}
    {showAppUtilities ? <PowerDialRunBar /> : null}
    </>
  );
}
